import { useEffect, useState } from 'react';
import { verVertical, type CampanhaCompleta } from '../../api/cptu';
import { GRANDEZAS, GRANDEZAS_DA_ESTATISTICA, estatisticaDasUnidades } from '../../cptu/motor';
import type { Camada } from '../../cptu/motor';
import { combinarMarcas, marcaDaColuna, numeroBr, textoDoValor } from '../../cptu/visual';
import type { VerticalDaAnalise } from '../../cptu/visual';
import { plural } from '../../formato';
import { ErroDaApi, Vazio } from '../../ui';
import { verticaisAtivas } from './analise';
import { NotasDoPorque } from './TabelaDaVertical';

/**
 * O perfil típico da campanha — onda 316 (a vista Perfis da Análise integrada; spec 07 §5.6): a tabela do
 * `estatisticaDasUnidades` do motor com as camadas VIGENTES de cada vertical (o `GET /cptu/verticais/:id` das que têm
 * camadas) — a unidade, as verticais em que aparece, as leituras e, por grandeza, média ± desvio, mínimo–máximo e CV,
 * com a marca do R7 da 313 (a pior das verticais) e o porquê numerado embaixo. As unidades se juntam sem caixa e sem
 * os espaços das pontas («U2 · Argila mole» e «u2 · argila mole » são a mesma).
 */
export function PerfilTipico({ c, verticais }: { c: CampanhaCompleta; verticais: readonly VerticalDaAnalise[] }) {
  const [camadas, setCamadas] = useState<Record<string, Camada[]> | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const comCamadas = verticaisAtivas(c).filter((v) => v.n_camadas > 0);
  const chave = comCamadas.map((v) => `${v.id}:${v.n_camadas}`).join('|');

  useEffect(() => {
    let vivo = true;
    setErro(null);
    void (async () => {
      try {
        const lidas = await Promise.all(comCamadas.map((v) => verVertical(v.id)));
        if (!vivo) return;
        setCamadas(Object.fromEntries(lidas.map((v) => [v.codigo, v.camadas.map((k): Camada => ({
          topo: k.topo_m, base: k.base_m, unidade: k.unidade, descricao: k.descricao, origem: k.origem,
        }))])));
      } catch (e) { if (vivo) setErro(e); }
    })();
    return () => { vivo = false; };
    // A chave resume as verticais com camadas e quantas: o mesmo conjunto não relê.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (comCamadas.length === 0) {
    return (
      <section className="cartao cptu-perfil-tipico" aria-label="Perfil típico">
        <h3 className="cptu-titulo-da-secao">Perfil típico</h3>
        <Vazio>Nenhuma vertical tem camadas gravadas — as camadas se gravam na vista Camadas de cada vertical.</Vazio>
      </section>
    );
  }
  if (!camadas) return <p className="cptu-nota" role="status">Lendo as camadas das verticais…</p>;

  const entram = verticais.map((v) => ({ codigo: v.codigo, r: v.r, camadas: camadas[v.codigo] ?? [] }));
  const unidades = estatisticaDasUnidades(entram);
  const grandezas = GRANDEZAS_DA_ESTATISTICA.filter((g) => verticais.every((v) => marcaDaColuna(g, v.r.premissas).mostrar));
  const marcas = grandezas.map((g) => combinarMarcas(verticais.map((v) => ({ marca: marcaDaColuna(g, v.r.premissas) }))));
  const motivos: string[] = [];
  for (const m of marcas) if (!m.homologada && m.motivo && !motivos.includes(m.motivo)) motivos.push(m.motivo);

  return (
    <section className="cartao cptu-perfil-tipico" aria-label="Perfil típico">
      <h3 className="cptu-titulo-da-secao">Perfil típico</h3>
      <p className="cptu-nota">
        As camadas gravadas de mesma unidade, em todas as verticais, juntam as leituras: média ± desvio amostral,
        mínimo–máximo e CV (desvio/|média|), sem as leituras que não têm valor.
      </p>
      <div className="rola">
        <table aria-label="Perfil típico por unidade">
          <thead>
            <tr>
              <th scope="col">Unidade</th>
              <th scope="col">Verticais</th>
              <th scope="col" className="n">Leituras</th>
              {grandezas.map((g, j) => {
                const m = marcas[j];
                const nota = !m.homologada && m.motivo ? motivos.indexOf(m.motivo) + 1 : null;
                return (
                  <th key={g} scope="col" className="n">
                    {GRANDEZAS[g].rotulo}{GRANDEZAS[g].unidade ? ` (${GRANDEZAS[g].unidade})` : ''}
                    {nota !== null && <span className="cptu-marca-texto"> ▲ ({nota})</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {unidades.map((u) => (
              <tr key={u.unidade.toLowerCase()} data-unidade={u.unidade}>
                <th scope="row">{u.unidade}</th>
                <td data-verticais={u.verticais.length}>
                  {plural(u.verticais.length, 'vertical', 'verticais')}
                  <span className="cptu-nota"> · {u.verticais.join(', ')}</span>
                </td>
                <td className="n mono">{numeroBr(u.leituras, 0)}</td>
                {grandezas.map((g) => {
                  const s = u.estatistica[g];
                  if (!s || s.n === 0) return <td key={g} className="n" data-grandeza={g}><span className="cptu-nota">não se aplica</span></td>;
                  return (
                    <td key={g} className="n mono cptu-celula-estatistica" data-grandeza={g} data-n={s.n} data-media={s.media}>
                      <span>{textoDoValor(g, s.media)} ± {textoDoValor(g, s.desvio)}</span>
                      <span>{textoDoValor(g, s.minimo)}–{textoDoValor(g, s.maximo)}</span>
                      <span>CV {Number.isFinite(s.cv) ? `${numeroBr(s.cv * 100, 1)} %` : '—'}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NotasDoPorque motivos={motivos} />
    </section>
  );
}
